import { clsx } from "clsx";
import { ScrollGridContainer } from "./ScrollGridContainer";
import { pxToRem } from "@ui/lib/utils";

export interface ScrollGridProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  className?: string;
  columns?: 3 | 4 | 5;
  alignNavButtonsToRatio?: number;
  legend?: { show: boolean; getItemTitle: (item: T) => string; textClassName?: string; className?: string };
}

export function ScrollGrid<T>({
  items,
  renderItem,
  className,
  columns = 3,
  alignNavButtonsToRatio,
  legend,
}: ScrollGridProps<T>) {
  const itemCount = items.length;
  const allVisibleOnDesktop = itemCount <= columns;

  // Internal options. Hard-coded for now to ensure consistency but
  // could be moved to props if needed.
  const snap = true;
  const draggable = true;
  const navButtons = true;
  const scrollbar = "none";
  const peek = false;

  return (
    <ScrollGridContainer
      scrollbar={scrollbar}
      snap={snap}
      draggable={draggable}
      itemCount={itemCount}
      columns={columns}
      navButtons={navButtons}
      peek={peek}
      alignNavButtonsToRatio={alignNavButtonsToRatio}
      {...(legend?.show && itemCount > 3
        ? {
            legend: {
              className: legend.className,
              textClassName: legend.textClassName,
              titles: items.map(legend.getItemTitle),
            },
          }
        : {})}
      style={
        {
          containerType: alignNavButtonsToRatio ? "inline-size" : undefined,
          "--gap": pxToRem(16),
        } as React.CSSProperties
      }
      className={clsx(
        className,
        [
          "tw-var-[container=100%]",
          alignNavButtonsToRatio &&
            "supports-[container-type:inline-size]:tw-var-[container=calc(100cqw-var(--full-bleed)*2)]",
        ],
        "tw-var-[w=min(var(--max,999rem),calc((var(--container)-(var(--gap)*(var(--cols)-1))-var(--peek))/var(--cols)))]",
        "tw-var-[peek=2rem] lg:tw-var-[peek=3rem]",
        columns === 3 && [
          "tw-var-[max=20rem] lg:tw-var-[max=unset]",
          "tw-var-[cols=1] sm:tw-var-[cols=2] lg:tw-var-[cols=3]",
          (!peek || allVisibleOnDesktop) && "lg:!tw-var-[peek=0px]",
        ],
        columns === 4 && [
          "tw-var-[max=20rem]",
          "tw-var-[cols=1] sm:tw-var-[cols=2] lg:tw-var-[cols=3] xl:tw-var-[cols=4]",
          (!peek || allVisibleOnDesktop) && "xl:!tw-var-[peek=0px]",
        ],
        columns === 5 && [
          "tw-var-[max=15rem]",
          "tw-var-[cols=1] min-[32rem]:tw-var-[cols=2] md:tw-var-[cols=3] lg:tw-var-[cols=4] xl:tw-var-[cols=5]",
          (!peek || allVisibleOnDesktop) && "xl:!tw-var-[peek=0px]",
        ],
      )}
    >
      {items.map((item, index) => (
        <li key={index} className={clsx("tw-flex-none tw-w-[var(--w)]", snap && "tw-snap-start")}>
          {renderItem(item, index)}
        </li>
      ))}
    </ScrollGridContainer>
  );
}
